/**
 * Onboarding (S10 to S19). docs/41 owns the steps and words; this is the flow. Every step saves as it goes, so
 * leaving and coming back resumes at the same place (the Get live card on Home links to the next step).
 */
import { Banner, Button, Card, ChoiceGroup, Icon, RecordField, SourceBadge, TextArea, TextField } from '@ff/app-ui';
import { Domain, Email, type onboarding as O } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText } from '../lib/api.ts';
import { invalidate, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { dayLabel } from '../lib/format.ts';
import { navigate } from '../lib/router.ts';
import { firstName, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { uploadBlob } from '../lib/upload.ts';
import { PhotoAdder, PhotoNeedsCard } from './photos.tsx';
import { InstallCard } from '../pwa/cards.tsx';
import { S } from '@ff/app-ui/copy';
import { Loaded, Screen } from '../ui/common.tsx';
import { FactValue } from '../ui/FactValue.tsx';
import { editDefault, editValue, provenance } from '../lib/facts.ts';

type Step = 'facts' | 'google' | 'domain' | 'brand' | 'photos' | 'licence' | 'go_live';
const STEPS: Step[] = ['facts', 'google', 'domain', 'brand', 'photos', 'licence', 'go_live'];
type Onboarding = z.infer<typeof O.Onboarding>;

function nextAfter(ob: Onboarding, step: Step): string {
  const i = STEPS.indexOf(step);
  const rest = [...STEPS.slice(i + 1), ...STEPS.slice(0, i)];
  const n = rest.find((s) => ob.steps.find((x) => x.step === s)?.state === 'todo' || ob.steps.find((x) => x.step === s)?.state === 'waiting_on_client');
  return n ? `/start/${n}` : '/';
}

export function StartScreen({ step }: { step: string | null }): JSX.Element {
  const q = useQuery('onboarding');
  const s = useReady();
  if (step === null) return <Welcome name={firstName(s.me)} />;
  if (step === 'live') return <Live />;
  if (!STEPS.includes(step as Step))
    return (
      <Screen title={S.app.notFoundTitle} back="/">
        <p>{S.app.notFoundBody}</p>
      </Screen>
    );
  const st = step as Step;
  const idx = STEPS.indexOf(st);
  return (
    <Screen title={S.getLive.stepTitle[st]!} back="/" query={q}>
      <div class="step-progress">
        <p class="t-small t-muted">
          {S.getLive.stepOf(idx + 1, STEPS.length)} · {S.getLive.stepTime[st]}
        </p>
        <div class="bar" role="progressbar" aria-valuemin={0} aria-valuemax={STEPS.length} aria-valuenow={idx + 1} aria-label={S.getLive.stepOf(idx + 1, STEPS.length)}>
          <span style={`inline-size:${((idx + 1) / STEPS.length) * 100}%`} />
        </div>
      </div>
      <Loaded q={q} skeleton="detail">
        {(ob) => {
          const done = (next = nextAfter(ob, st)) => {
            haptic();
            invalidate('home', 'onboarding');
            navigate(st === 'go_live' ? '/start/live' : next);
          };
          const state = ob.steps.find((x) => x.step === st)?.state;
          return (
            <div class="stack" data-gap="l">
              {state === 'done' && <Banner tone="ok" title={S.getLive.state.done} />}
              {state === 'waiting_on_us' && <Banner tone="info" icon="hourglass" title={S.getLive.state.waiting_on_us} />}
              {st === 'facts' && <FactsStep onDone={done} />}
              {st === 'google' && <GoogleStep ob={ob} onDone={done} />}
              {st === 'domain' && <DomainStep onDone={done} />}
              {st === 'brand' && <BrandStep ob={ob} onDone={done} />}
              {st === 'photos' && <PhotosStep ob={ob} onDone={done} />}
              {st === 'licence' && <LicenceStep onDone={done} />}
              {st === 'go_live' && <GoLiveStep ob={ob} onDone={done} />}
              <div class="stack" data-gap="s">
                {ob.bookingUrl && (
                  <Button href={ob.bookingUrl} external variant="quiet" icon="calendar">
                    {S.getLive.stuck}
                  </Button>
                )}
                <Button href="/" variant="quiet" icon="chevron-left">
                  {S.getLive.saveAndLater}
                </Button>
              </div>
            </div>
          );
        }}
      </Loaded>
    </Screen>
  );
}

/** The 7 day clock's pauses (c041): how long so far, and what it's waiting on now, from the jobs clock. */
function ClockPauses({ run }: { run: Onboarding['run'] }): JSX.Element | null {
  const C = S.getLive.clock;
  const reasons = run.pausedFor ?? [];
  if (!run.clockStartedAt || run.liveAt || (!reasons.length && !run.pausedDays)) return null;
  return (
    <div class="stack" data-gap="s" role="status">
      {reasons.map((r) => (
        <p key={r} class="t-small">
          <Icon name="hourglass" size={16} /> {C.pausedFor[r] ?? C.pausedNow}
        </p>
      ))}
      {!!run.pausedDays && <p class="t-small t-muted">{C.pausedSoFar(run.pausedDays)}</p>}
    </div>
  );
}

function Welcome({ name }: { name: string | null }): JSX.Element {
  const q = useQuery('onboarding');
  return (
    <Screen title={S.getLive.welcomeTitle(name)}>
      <div class="stack" data-gap="l">
        <p class="t-heading welcome-lead">{S.getLive.welcomeLead}</p>
        <p class="t-muted">{S.getLive.welcomeTime}</p>
        <Button variant="primary" size="lg" block iconEnd="arrow-right" href={q.data ? nextAfter(q.data, 'go_live') : '/start/facts'}>
          {S.getLive.welcomeGo}
        </Button>
      </div>
    </Screen>
  );
}

function Live(): JSX.Element {
  const own = useQuery('ownership');
  const domain = own.data?.domain.name ?? '';
  return (
    <Screen title={S.getLive.liveTitle}>
      <div class="stack" data-gap="l">
        <p class="t-heading">{S.getLive.liveLead(domain)}</p>
        <h2 class="t-heading">{S.getLive.liveNext}</h2>
        <ol role="list" class="stack" data-gap="s">
          <li><Button block href="/account/notifications" icon="bell">{S.getLive.liveNextItems[0]}</Button></li>
          <li><Button block href="/reviews/ask" icon="star">{S.getLive.liveNextItems[1]}</Button></li>
          <li><Button block href="/jobs/new" icon="camera">{S.getLive.liveNextItems[2]}</Button></li>
        </ol>
        <InstallCard force />
      </div>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ S12 facts

function FactsStep({ onDone }: { onDone: () => void }): JSX.Element {
  const q = useQuery('facts');
  const act = useSend();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [rebuilding, setRebuilding] = useState<'idle' | 'updating' | 'updated'>('idle');
  // A structured fact (an address, hours, a license) keeps its value: the words go to our team as a note, never as the new value
  const decide = async (factId: string, decision: 'confirm' | 'edit' | 'not_sure', v?: string, keep?: unknown) => {
    const item = decision === 'edit' ? (keep !== undefined ? { factId, decision, value: keep as never, note: (v ?? '').slice(0, 500) } : { factId, decision, value: v ?? '' }) : { factId, decision };
    const out = await act.run(() => send('factsConfirm', { body: { items: [item] } }));
    if (!out) return;
    setEditing(null);
    if (out.previewRebuilding && decision === 'edit') {
      setRebuilding('updating');
      setTimeout(() => setRebuilding('updated'), 1500);
    }
    await q.refetch();
  };
  return (
    <>
      <div class="stack" data-gap="s">
        <h2 class="t-heading">{S.getLive.factsTitle}</h2>
        <p>{S.getLive.factsLead}</p>
      </div>
      {rebuilding !== 'idle' && (
        <p class="t-small" role="status">
          <Icon name={rebuilding === 'updated' ? 'check' : 'refresh'} size={16} /> {rebuilding === 'updated' ? S.getLive.updatedPreview : S.getLive.updatingPreview}
        </p>
      )}
      <Loaded q={q} skeleton="cards">
        {(d) => (
          <div class="stack" data-gap="l">
            {d.groups.map((g) => (
              <section key={g.group} class="stack" data-gap="s" aria-labelledby={`fg-${g.group}`}>
                <div class="list-head">
                  <h3 id={`fg-${g.group}`} class="t-heading">{S.getLive.group[g.group]}</h3>
                  {g.done && <span class="chip" data-tone="ok"><Icon name="check" size={14} /> {S.getLive.factsDone}</span>}
                </div>
                {g.facts.map((f) => (
                  <Card key={f.id} class="fact">
                    <p class="t-small t-muted">{f.label}</p>
                    {editing === f.id ? (
                      <form
                        class="stack"
                        data-gap="s"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (value.trim()) void (editDefault(f.path, f.value) === null ? decide(f.id, 'edit', value.trim(), f.value) : decide(f.id, 'edit', editValue(f.value, value)));
                        }}
                      >
                        {editDefault(f.path, f.value) === null && (
                          <>
                            <FactValue path={f.path} value={f.value} />
                            <p class="t-small t-muted">{S.facts.structuredEdit}</p>
                          </>
                        )}
                        <TextField label={editDefault(f.path, f.value) === null ? S.facts.whatChanged : S.getLive.newValue} value={value} onInput={setValue} maxLength={300} />
                        <div class="btn-row" data-equal>
                          <Button type="submit" variant="primary" busy={act.busy}>{S.app.save}</Button>
                          <Button variant="quiet" onClick={() => setEditing(null)}>{S.app.cancel}</Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <FactValue path={f.path} value={f.value} />
                        <SourceBadge icon={f.source.kind === 'register' ? 'shield' : f.source.kind === 'gbp' ? 'globe' : 'user'}>{provenance(f.source, f.checkedAt)}</SourceBadge>
                        {f.confirmation === 'pending' ? (
                          <div class="fact-actions">
                            <Button variant="primary" size="sm" icon="check" busy={act.busy} onClick={() => void decide(f.id, 'confirm')}>{S.getLive.right}</Button>
                            <Button size="sm" icon="edit" onClick={() => (setEditing(f.id), setValue(editDefault(f.path, f.value) ?? ''))}>{S.getLive.fix}</Button>
                            <Button size="sm" variant="quiet" onClick={() => void decide(f.id, 'not_sure')}>{S.getLive.notSure}</Button>
                          </div>
                        ) : (
                          <p class="t-small fact-state">
                            <Icon name={f.confirmation === 'not_sure' ? 'help' : 'check'} size={16} /> {f.confirmation === 'not_sure' ? S.getLive.notSureMarked : f.confirmation === 'edited' ? S.getLive.updatedPreview : S.getLive.ticked}
                            <button type="button" class="link-btn" onClick={() => (setEditing(f.id), setValue(editDefault(f.path, f.value) ?? ''))}>{S.app.change}</button>
                          </p>
                        )}
                      </>
                    )}
                  </Card>
                ))}
              </section>
            ))}
            {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
            <Button variant="primary" size="lg" block iconEnd="arrow-right" disabled={!d.groups.every((g) => g.done)} onClick={onDone}>
              {S.app.next}
            </Button>
          </div>
        )}
      </Loaded>
    </>
  );
}

// ------------------------------------------------------------------------------------------ S13 Google

function stepAct(step: Step, body: z.input<typeof O.OnboardingStepBody>) {
  return send('onboardingStep', { params: { step }, body }).then((r) => {
    setCached('onboarding', {}, () => r.onboarding);
    return r;
  });
}

function GoogleStep({ ob, onDone }: { ob: Onboarding; onDone: () => void }): JSX.Element {
  const act = useSend();
  const [sent, setSent] = useState(ob.steps.find((s) => s.step === 'google')?.state === 'waiting_on_us');
  const [why, setWhy] = useState(false);
  return (
    <>
      <p>{S.getLive.googleLead}</p>
      {ob.googleInviteAddress && <RecordField label={S.getLive.googleAddress} value={ob.googleInviteAddress} copyLabel={S.app.copy} copiedLabel={S.app.copied} />}
      <ol role="list" class="steps">
        {S.getLive.googleSteps.map((t, i) => (
          <li key={i}>
            <span class="step-n" aria-hidden="true">{i + 1}</span>
            <p>{t}</p>
          </li>
        ))}
      </ol>
      <button type="button" class="link-btn" aria-expanded={why} onClick={() => setWhy(!why)}>
        {S.getLive.googleWhichLogin}
      </button>
      {why && <p class="t-muted">{S.getLive.googleWhichLoginBody}</p>}
      {sent ? (
        <>
          <Banner tone="ok" title={S.getLive.googleThanks} />
          <Button variant="primary" size="lg" block iconEnd="arrow-right" onClick={() => onDone()}>{S.app.next}</Button>
        </>
      ) : (
        <Button
          variant="primary"
          size="lg"
          block
          busy={act.busy}
          onClick={async () => {
            if (await act.run(() => stepAct('google', { action: 'google_invite_sent' }))) setSent(true);
          }}
        >
          {S.getLive.googleSent}
        </Button>
      )}
      {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
    </>
  );
}

// ------------------------------------------------------------------------------------------ S14 domain

type Registrar = z.infer<typeof O.Registrar>;
const REGISTRARS: Registrar[] = ['godaddy', 'namecheap', 'squarespace', 'wix', 'cloudflare', 'other', 'none', 'web_person'];

function DomainStep({ onDone }: { onDone: () => void }): JSX.Element {
  const [domain, setDomain] = useState('');
  const [domainErr, setDomainErr] = useState<string | null>(null);
  const [registrar, setRegistrar] = useState<Registrar | null>(null);
  const [designer, setDesigner] = useState('');
  const [designerErr, setDesignerErr] = useState<string | null>(null);
  const [check, setCheck] = useState<z.infer<typeof O.DomainCheck> | null>(null);
  const [polling, setPolling] = useState(false);
  const act = useSend();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const runCheck = async (d: string) => {
    const r = await act.run(() => send('domainCheck', { body: { domain: d } }));
    if (!r) return;
    setCheck(r);
    if (r.status !== 'active') {
      setPolling(true);
      timer.current = setTimeout(() => void runCheck(d), 15_000);
    } else setPolling(false);
  };

  const parsed = () => {
    const p = Domain.safeParse(domain.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, ''));
    if (!p.success) {
      setDomainErr(S.getLive.domainHint);
      return null;
    }
    setDomainErr(null);
    return p.data;
  };

  return (
    <>
      <div class="stack" data-gap="s">
        <h2 class="t-heading">{S.getLive.domainTitle}</h2>
        <p>{S.getLive.domainLead}</p>
      </div>
      <form
        class="stack"
        data-gap="s"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const d = parsed();
          if (d) await runCheck(d);
        }}
      >
        <TextField label={S.getLive.domainField} hint={S.getLive.domainHint} inputMode="url" autoCapitalize="none" spellcheck={false} value={domain} onInput={setDomain} error={domainErr} mono />
        <Button type="submit" icon="search" busy={act.busy && !check}>{S.getLive.domainLookup}</Button>
      </form>
      <ChoiceGroup legend={S.getLive.domainTitle} legendClass="sr-only" value={registrar ?? (check?.registrar?.toLowerCase() as Registrar | undefined) ?? null} onChange={setRegistrar} options={REGISTRARS.map((r) => ({ value: r, label: S.getLive.registrar[r]! }))} />
      <p class="t-small t-muted"><Icon name="mail" size={16} /> {S.getLive.emailSafe}</p>

      {registrar === 'web_person' ? (
        <form
          class="stack"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            const d = parsed();
            const em = Email.safeParse(designer.trim());
            if (!em.success) return setDesignerErr(S.signin.emailError);
            setDesignerErr(null);
            if (!d) return;
            if (await act.run(() => stepAct('domain', { action: 'domain_path', domain: d, registrar: 'web_person', designerEmail: em.data }))) {
              toast(S.getLive.webPersonSent(em.data));
              onDone();
            }
          }}
        >
          <TextField label={S.getLive.webPersonEmail} type="email" inputMode="email" autoCapitalize="none" value={designer} onInput={setDesigner} error={designerErr} />
          <Button type="submit" variant="primary" size="lg" block busy={act.busy}>{S.getLive.webPersonSend}</Button>
        </form>
      ) : (
        check && (
          <Card>
            <h3 class="t-heading">{check.status === 'active' ? S.getLive.domainAllGood : S.getLive.domainRecords}</h3>
            {check.status !== 'active' && <p class="t-muted">{S.getLive.domainRecordsLead}</p>}
            <ul role="list" class="stack records">
              {check.records.map((r) => (
                <li key={r.type + r.name} class="stack" data-gap="s">
                  <p class="record-check" data-ok={r.ok ? '' : undefined}>
                    <Icon name={r.ok ? 'check' : 'hourglass'} size={18} /> <strong>{r.type}</strong> · {r.ok ? S.getLive.domainFound : S.getLive.domainNotYet}
                  </p>
                  <RecordField label={S.getLive.recordName} value={r.name} copyLabel={S.app.copy} copiedLabel={S.app.copied} />
                  <RecordField label={S.getLive.recordValue} value={r.expected} copyLabel={S.app.copy} copiedLabel={S.app.copied} />
                </li>
              ))}
            </ul>
            {polling && (
              <div class="stack" data-gap="s" role="status">
                <p><strong>{S.getLive.domainChecking}</strong></p>
                <p class="t-muted">{S.getLive.domainCheckingLead}</p>
              </div>
            )}
            <div class="card-foot btn-row">
              {check.status === 'active' ? (
                <Button
                  variant="primary"
                  iconEnd="arrow-right"
                  busy={act.busy}
                  onClick={async () => {
                    if (await act.run(() => stepAct('domain', { action: 'domain_path', domain: check.domain, registrar: registrar ?? 'other' }))) onDone();
                  }}
                >
                  {S.app.next}
                </Button>
              ) : (
                <Button icon="refresh" busy={act.busy} onClick={() => void runCheck(check.domain)}>{S.getLive.domainCheckNow}</Button>
              )}
            </div>
          </Card>
        )
      )}
      {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
    </>
  );
}

// ------------------------------------------------------------------------------------------ S15 brand check

interface Pin {
  x: number;
  y: number;
  note: string;
}

function BrandStep({ ob, onDone }: { ob: Onboarding; onDone: () => void }): JSX.Element {
  const [view, setView] = useState<'phone' | 'desktop'>('phone');
  const [pins, setPins] = useState<Pin[]>([]);
  const [logoOk, setLogoOk] = useState<'yes' | 'no' | null>(null);
  const [colorsOk, setColorsOk] = useState<'yes' | 'no' | null>(null);
  const [general, setGeneral] = useState('');
  const act = useSend();
  const submit = async () => {
    const all = [...pins, ...(general.trim() ? [{ x: 0.5, y: 0.5, note: general.trim() }] : [])];
    const out = await act.run(() => stepAct('brand', { action: 'brand_check', logoOk: logoOk !== 'no', colorsOk: colorsOk !== 'no', pins: all.map((p) => ({ page: '/', x: p.x, y: p.y, note: p.note })) }));
    if (out) onDone();
  };
  return (
    <>
      <div class="stack" data-gap="s">
        <h2 class="t-heading">{S.getLive.brandTitle}</h2>
        <p>{S.getLive.brandLead}</p>
        <p class="t-small t-muted">{S.getLive.round(ob.correctionRound.current + 1, ob.correctionRound.max)}</p>
      </div>
      <div class="segmented" role="group" aria-label={S.getLive.tapToComment}>
        <button type="button" aria-pressed={view === 'phone'} onClick={() => setView('phone')}>{S.getLive.previewPhone}</button>
        <button type="button" aria-pressed={view === 'desktop'} onClick={() => setView('desktop')}>{S.getLive.previewDesktop}</button>
      </div>
      <div class="preview-frame" data-view={view}>
        {ob.previewUrl ? (
          <>
            <iframe src={ob.previewUrl} title={S.getLive.preview} loading="lazy" sandbox="allow-same-origin allow-scripts" referrerpolicy="no-referrer" />
            <button
              type="button"
              class="pin-layer"
              aria-label={S.getLive.addNote}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const ev = e as unknown as MouseEvent;
                setPins((p) => [...p, { x: (ev.clientX - r.left) / r.width, y: (ev.clientY - r.top) / r.height, note: '' }]);
              }}
            />
            {pins.map((p, i) => (
              <span key={i} class="pin" style={`left:${p.x * 100}%;top:${p.y * 100}%`} aria-hidden="true">{i + 1}</span>
            ))}
          </>
        ) : (
          <p class="t-muted preview-empty">{S.getLive.previewNotReady}</p>
        )}
      </div>
      {pins.map((p, i) => (
        <TextField key={i} label={S.getLive.pinAt(i + 1)} placeholder={S.getLive.notePlaceholder} value={p.note} onInput={(v) => setPins((ps) => ps.map((x, j) => (j === i ? { ...x, note: v } : x)))} />
      ))}
      <ChoiceGroup legend={S.getLive.logoOk} value={logoOk} onChange={setLogoOk} options={[{ value: 'yes', label: S.app.yes }, { value: 'no', label: S.app.no }]} />
      <ChoiceGroup legend={S.getLive.colorsOk} value={colorsOk} onChange={setColorsOk} options={[{ value: 'yes', label: S.app.yes }, { value: 'no', label: S.app.no }]} />
      <TextArea label={S.getLive.anythingWrong} placeholder={S.getLive.notePlaceholder} value={general} onInput={setGeneral} maxLength={1000} rows={3} />
      {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
      <Button variant="primary" size="lg" block busy={act.busy} onClick={() => void submit()}>
        {pins.some((p) => p.note.trim()) || general.trim() || logoOk === 'no' || colorsOk === 'no' ? S.getLive.sendNotes : S.getLive.looksGood}
      </Button>
    </>
  );
}

// ------------------------------------------------------------------------------------------ S16 photos

/**
 * Photos gate go-live (c057). The step isn't a box to tick: it follows the photos rule, so the screen says exactly
 * what the site still needs, takes the photos (with what they show), and turns to "we're checking them" by itself
 * once they've sent enough. It's done when a person here has approved enough (k11).
 */
function PhotosStep({ ob, onDone }: { ob: Onboarding; onDone: () => void }): JSX.Element {
  const act = useSend();
  const opts = useQuery('jobOptions', {});
  const needs = ob.photoNeeds;
  const state = ob.steps.find((x) => x.step === 'photos')?.state;
  const allSent = !!needs && needs.missing.length === 0;
  return (
    <>
      <p>{S.getLive.photosLead}</p>
      <ul class="bullets">
        {S.getLive.photosBrief.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
      {needs && <PhotoNeedsCard needs={needs} />}
      {/* Photos are added in the app (deck ruling c043): no email or text address for them. */}
      {state !== 'done' && <PhotoAdder services={opts.data?.services} onSent={() => invalidate('onboarding', 'home', 'media')} />}
      <a class="section-link" href="/jobs/photos">
        <Icon name="image" size={20} /> {S.getLive.photosSeeAll}
      </a>
      {state !== 'done' && state !== 'waiting_on_us' && (
        <>
          {!allSent && <p class="t-small t-muted">{S.getLive.photosNotYet}</p>}
          <Button size="lg" block busy={act.busy} disabled={!allSent} onClick={async () => (await act.run(() => stepAct('photos', { action: 'photos_done' }))) && onDone()}>
            {S.getLive.photosDone}
          </Button>
          {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
        </>
      )}
      {state === 'waiting_on_us' && <p role="status">{S.getLive.photosAllSent}</p>}
    </>
  );
}

// ------------------------------------------------------------------------------------------ S17 licence and insurance

function LicenceStep({ onDone }: { onDone: () => void }): JSX.Element {
  const facts = useQuery('facts');
  const act = useSend();
  const [wrong, setWrong] = useState(false);
  const [note, setNote] = useState('');
  const [expiry, setExpiry] = useState('');
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const licence = facts.data?.groups.flatMap((g) => g.facts).find((f) => f.source.kind === 'register');
  return (
    <>
      {licence && (
        <Card>
          <p class="t-small t-muted">{S.getLive.licenceLead}</p>
          <p class="fact-value">{String(licence.value)}</p>
          <SourceBadge icon="shield">{licence.source.label}</SourceBadge>
          {!confirmed && !wrong && (
            <div class="card-foot btn-row" data-equal>
              <Button variant="primary" icon="check" busy={act.busy} onClick={async () => (await act.run(() => stepAct('licence', { action: 'licence_confirm', correct: true }))) && setConfirmed(true)}>
                {S.getLive.right}
              </Button>
              <Button onClick={() => setWrong(true)}>{S.getLive.licenceWrong}</Button>
            </div>
          )}
          {wrong && !confirmed && (
            <div class="stack" data-gap="s">
              <TextArea label={S.getLive.notePlaceholder} value={note} onInput={setNote} maxLength={500} rows={2} />
              <Button variant="primary" busy={act.busy} onClick={async () => (await act.run(() => stepAct('licence', { action: 'licence_confirm', correct: false, ...(note.trim() ? { note: note.trim() } : {}) }))) && setConfirmed(true)}>
                {S.inbox.send}
              </Button>
            </div>
          )}
          {confirmed && <p class="fact-state"><Icon name="check" size={16} /> {S.getLive.ticked}</p>}
        </Card>
      )}
      <Card>
        <h2 class="t-heading">{S.getLive.insurance}</h2>
        <p class="t-muted">{S.getLive.insuranceLead}</p>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          hidden
          onChange={async (e) => {
            const f = (e.currentTarget as HTMLInputElement).files?.[0];
            if (!f) return;
            const id = await act.run(() => uploadBlob(f, f.type, 'document', 'insurance', () => undefined));
            if (id) setUploadId(id);
          }}
        />
        <div class="stack" data-gap="s">
          <Button icon={uploadId ? 'check' : 'upload'} busy={act.busy} onClick={() => input.current?.click()}>{S.getLive.insuranceUpload}</Button>
          <TextField label={S.getLive.insuranceExpiry} type="date" value={expiry} onInput={setExpiry} />
        </div>
      </Card>
      {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
      <Button
        variant="primary"
        size="lg"
        block
        iconEnd="arrow-right"
        busy={act.busy}
        onClick={async () => {
          if (uploadId && expiry) await act.run(() => stepAct('licence', { action: 'insurance_upload', uploadId, expiresOn: expiry }));
          onDone();
        }}
      >
        {S.app.next}
      </Button>
    </>
  );
}

// ------------------------------------------------------------------------------------------ S18 go-live

function GoLiveStep({ ob, onDone }: { ob: Onboarding; onDone: () => void }): JSX.Element {
  const own = useQuery('ownership');
  const act = useSend();
  const zone = useStore(tz);
  void zone;
  const domain = own.data?.domain.name ?? '';
  const names = ob.approvers.map((a) => a.name).join(' or ');
  return (
    <>
      <h2 class="t-heading">{S.getLive.goLiveTitle}</h2>
      <p>{S.getLive.goLiveLead}</p>
      <ul class="bullets">
        {S.getLive.goLiveBullets(domain || '').map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
      <p><strong>{S.getLive.goLiveEmail}</strong></p>
      {ob.run.deadlineAt && <p class="t-muted">{S.getLive.goLiveBy(dayLabel(ob.run.deadlineAt.slice(0, 10)))}</p>}
      <ClockPauses run={ob.run} />
      {ob.canApproveGoLive ? (
        <div class="stack" data-gap="s">
          {ob.previewUrl && <Button href={ob.previewUrl} external icon="eye" block>{S.getLive.lookAgain}</Button>}
          <Button variant="primary" size="lg" block busy={act.busy} icon="globe" onClick={async () => (await act.run(() => stepAct('go_live', { action: 'go_live_approve' }))) && onDone()}>
            {S.getLive.goLive}
          </Button>
        </div>
      ) : (
        <div class="stack" data-gap="s">
          <Banner tone="info" icon="lock" title={S.getLive.goLiveWho(names)} />
          {ob.approvers.map((a) => (
            <Button key={a.userId} busy={act.busy} onClick={async () => (await act.run(() => stepAct('go_live', { action: 'remind_approver', userId: a.userId }))) && toast(S.getLive.reminded)}>
              {S.getLive.remind(a.name.split(' ')[0]!)}
            </Button>
          ))}
        </div>
      )}
      {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
    </>
  );
}
